import { useState } from 'react'
import type { NewsItem } from '../types/news.types'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Share2, Copy, Check, Twitter, Facebook, Linkedin, MessageCircle } from 'lucide-react'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Share URL Generation
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface ShareUrlParams {
  article: NewsItem
  platform: string
}

function generateShareUrl({ article, platform }: ShareUrlParams): string {
  const baseUrl = article.link
  const utmParams = new URLSearchParams({
    utm_source: 'social',
    utm_medium: 'share',
    utm_campaign: 'keynews',
    utm_content: platform,
  })

  return `${baseUrl}?${utmParams.toString()}`
}

function cleanHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/&quot;/g, '"')
}

function generateShareText(article: NewsItem): string {
  const title = cleanHtml(article.title)
  return `헤드라이너에서 발견한 기사: ${title}`
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Platform Share URLs
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function getTwitterShareUrl(article: NewsItem): string {
  const shareUrl = generateShareUrl({ article, platform: 'twitter' })
  const shareText = generateShareText(article)
  const appUrl = window.location.origin

  const twitterParams = new URLSearchParams({
    text: `${shareText}\n\n${appUrl}`,
    url: shareUrl,
  })

  return `https://twitter.com/intent/tweet?${twitterParams.toString()}`
}

function getFacebookShareUrl(article: NewsItem): string {
  const shareUrl = generateShareUrl({ article, platform: 'facebook' })

  const facebookParams = new URLSearchParams({
    u: shareUrl,
  })

  return `https://www.facebook.com/sharer/sharer.php?${facebookParams.toString()}`
}

function getLinkedInShareUrl(article: NewsItem): string {
  const shareUrl = generateShareUrl({ article, platform: 'linkedin' })

  const linkedInParams = new URLSearchParams({
    url: shareUrl,
  })

  return `https://www.linkedin.com/sharing/share-offsite/?${linkedInParams.toString()}`
}

function getKakaoShareUrl(article: NewsItem): string {
  const shareUrl = generateShareUrl({ article, platform: 'kakao' })
  const shareText = generateShareText(article)

  // Kakao requires SDK initialization, fallback to simple URL sharing
  // For full Kakao integration, consider using Kakao SDK
  const kakaoParams = new URLSearchParams({
    url: shareUrl,
    text: shareText,
  })

  return `https://story.kakao.com/share?${kakaoParams.toString()}`
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ShareButton Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface ShareButtonProps {
  article: NewsItem
  size?: 'default' | 'sm' | 'lg' | 'icon'
  variant?: 'default' | 'ghost' | 'outline'
}

export function ShareButton({ article, size = 'sm', variant = 'ghost' }: ShareButtonProps) {
  const [copied, setCopied] = useState(false)

  const handleCopyLink = async () => {
    const shareUrl = generateShareUrl({ article, platform: 'copy' })

    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopied(false)
      }, 2000)
    } catch (err) {
      console.error('Failed to copy link:', err)
    }
  }

  const handlePlatformShare = (platform: string, url: string) => {
    // Track share event (future analytics integration)
    console.log(`Sharing to ${platform}:`, url)

    // Open share URL in new window
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=400')
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size}>
          <Share2 className="h-3 w-3" />
          <span className="ml-1.5">공유</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>소셜 공유</DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => handlePlatformShare('twitter', getTwitterShareUrl(article))}
        >
          <Twitter className="mr-2 h-4 w-4" />
          <span>Twitter</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => handlePlatformShare('facebook', getFacebookShareUrl(article))}
        >
          <Facebook className="mr-2 h-4 w-4" />
          <span>Facebook</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => handlePlatformShare('linkedin', getLinkedInShareUrl(article))}
        >
          <Linkedin className="mr-2 h-4 w-4" />
          <span>LinkedIn</span>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={() => handlePlatformShare('kakao', getKakaoShareUrl(article))}>
          <MessageCircle className="mr-2 h-4 w-4" />
          <span>Kakao</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={handleCopyLink}>
          {copied ? (
            <>
              <Check className="mr-2 h-4 w-4 text-green-600" />
              <span>링크 복사됨!</span>
            </>
          ) : (
            <>
              <Copy className="mr-2 h-4 w-4" />
              <span>링크 복사</span>
            </>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
